import qs from 'qs';
import { Idea, Comment, SortOption, FilterOptions, Tag, Space } from '../types';
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { getTenantId } from './firebase-config';

// Environment configuration
const isDevelopment = process.env.REACT_APP_NODE_ENV === 'development' || false;
const demoMode = process.env.REACT_APP_DEMO_MODE === 'true' || false;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://severance-api-720810865761.us-east4.run.app';
const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/api';
const API_VERSION = process.env.REACT_APP_API_VERSION || 'v1';
const SUPER_USER_ID = process.env.REACT_APP_SUPER_USER_ID || '00000000-0000-0000-0000-000000000001';

// Cache for database UUIDs to prevent repeated API calls
const databaseUuidCache: Record<string, string> = {};

// User profile type
export interface UserProfile {
  uid: string;  // Firebase UID
  displayName: string;
  connection: string;
  email: string;
  createdAt?: Date;
  database_uuid?: string;  // The database user_id in UUID format
}

// API Client class
export class ApiClient {
  private client: AxiosInstance;
  private baseUrl: string;
  private authToken: string | null = null;

  constructor(baseUrl = API_BASE_URL, prefix = API_PREFIX, version = API_VERSION) {
    this.baseUrl = `${baseUrl}${prefix}/${version}`;
    this.client = axios.create({
      baseURL: this.baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    // Add request interceptor to attach auth token
    this.client.interceptors.request.use(
      async (config) => {
        if (this.authToken) {
          config.headers['Authorization'] = `Bearer ${this.authToken}`;
        }
        
        // Add tenant ID header - dynamically determined
        const tenantId = getTenantId();
        config.headers['X-Tenant-ID'] = tenantId;
        
        // Log all request details
        console.log(`🚀 API Request:`, {
          method: config.method?.toUpperCase(),
          url: config.url,
          headers: config.headers,
          params: config.params,
          data: config.data,
        });
        
        return config;
      },
      (error) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
      }
    );
    
    // Add response interceptor to log responses
    this.client.interceptors.response.use(
      (response) => {
        console.log(`✅ API Response:`, {
          status: response.status,
          statusText: response.statusText,
          url: response.config.url,
          method: response.config.method?.toUpperCase(),
          data: response.data,
        });
        return response;
      },
      (error) => {
        console.error(`❌ API Error:`, {
          message: error.message,
          url: error.config?.url,
          method: error.config?.method?.toUpperCase(),
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
        });
        return Promise.reject(error);
      }
    );
    
    // Call ensure-super-user on startup if in demo mode
    if (demoMode) {
      this.ensureSuperUser().catch(err => {
        console.error('Failed to ensure super user exists:', err);
      });
    }
  }

  // Set auth token for API requests
  setAuthToken(token: string | null) {
    this.authToken = token;
    console.log('Auth token updated:', token ? 'Token set' : 'Token cleared');
  }

  // Call the ensure-super-user endpoint
  async ensureSuperUser(): Promise<any> {
    try {
      const response = await this.client.get('/users/ensure-super-user');
      console.log('Super user ensured:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error ensuring super user:', error);
      throw error;
    }
  }

  // Generic API methods
  private async list<T>(endpoint: string, params?: any): Promise<T[]> {
    const response = await this.client.get<T[]>(endpoint, { params, paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }) });
    return response.data;
  }

  private async get<T>(endpoint: string, id: string, userId?: string): Promise<T | null> {
    try {
      const response = await this.client.get<T>(`${endpoint}/${id}`, { params: { user_id: userId } });
      return response.data;
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  private async create<T, U>(endpoint: string, data: T): Promise<U> {
    console.log(`Creating ${endpoint} with data:`, JSON.stringify(data, null, 2));
    const response = await this.client.post<U>(endpoint, data);
    return response.data;
  }

  private async update<T, U>(endpoint: string, id: string, data: T): Promise<U> {
    const response = await this.client.put<U>(`${endpoint}/${id}`, data);
    return response.data;
  }

  private async delete<T>(endpoint: string, id: string): Promise<T> {
    const response = await this.client.delete<T>(`${endpoint}/${id}`);
    return response.data;
  }

  // Ideas endpoints
  async listIdeas(sort: SortOption, filters?: FilterOptions, userId?: string): Promise<Idea[]> {
    if (userId) {
      return await this.list<Idea>('/ideas', { sort, ...filters, user_id: userId });
    }
    return await this.list<Idea>('/ideas', { sort, ...filters });
  }

  async getIdea(id: string, userId?: string): Promise<Idea | null> {
    if (isDevelopment) {
      await delay(300);
      const idea = mockIdeas.find(idea => idea.id === id);
      return idea || null;
    }
    if (userId) {
      return this.get<Idea>('/ideas', id, userId);
    }
    return this.get<Idea>('/ideas', id);
  }

  async createIdea(idea: Omit<Idea, 'id' | 'upvotes' | 'commentsCount' | 'created_at' | 'likes'>): Promise<Idea> {
    // Format request according to the backend's expected schema
    const ideaRequest = {
      title: idea.title,
      description: idea.description,
      // Don't include user_id in the request body
      // Format tags as objects with just the name field as expected by the backend
      tags: idea.tags?.map(tag => ({ name: tag.name, id: tag.id })) || [],
      // Include space_id for tenant filtering
      space_id: idea.space_id
    };
    
    console.log('Formatted request for backend:', JSON.stringify(ideaRequest, null, 2));
    
    // Extract user_id to pass as a separate parameter
    const userId = idea.user_id;
    
    // Use the private create method that allows setting query parameters
    const response = await this.client.post<Idea>('/ideas', ideaRequest, {
      params: { user_id: userId }
    });
    
    return response.data;
  }

  async updateIdea(id: string, idea: Partial<Idea>): Promise<Idea> {
    return this.update<Partial<Idea>, Idea>('/ideas', id, idea);
  }

  async uploadFileToIdea(ideaId: string, file: File): Promise<Idea> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await this.client.post<Idea>(
      `/ideas/${ideaId}/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  async deleteIdea(id: string): Promise<Idea> {
    return this.delete<Idea>('/ideas', id);
  }

  async toggleUpvote(ideaId: string, likedByUser: boolean | undefined = undefined): Promise<Idea | null> {
    try {
      // Ensure likedByUser is explicitly a boolean, not undefined
      const like = likedByUser === undefined ? false : likedByUser;

      
      const response = await this.client.patch<Idea>(`/ideas/${ideaId}/like/toggle`, { 
        like: like 
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async listIdeaComments(ideaId: string): Promise<Comment[]> {
    return this.list<Comment>(`/ideas/${ideaId}/comments`);
  }
  
  // Comments endpoints
  async listComments(ideaId: string): Promise<Comment[]> {
    // TODO: actually filter for idea
    return this.list<Comment>('/comments', { ideaId });
  }

  async getComment(id: string): Promise<Comment | null> {
    return this.get<Comment>('/comments', id);
  }

  async createComment(comment: { idea_id: string; comment_text: string; }): Promise<Comment> {
    try {
      console.log('ApiClient. called with:', { comment });
      
      // Send only idea_id and comment_text in the body
      // The backend will extract the user_id from the auth token
      const response = await this.client.post<Comment>('/comments', 
        { 
          idea_id: comment.idea_id, 
          comment_text: comment.comment_text,
        }
      );
      
      console.log('Comment creation response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating comment:', error);
      throw error;
    }
  }

  async updateComment(id: string, comment: Partial<Comment>): Promise<Comment> {
    return this.update<Partial<Comment>, Comment>('/comments', id, comment);
  }

  async deleteComment(id: string): Promise<Comment> {
    return this.delete<Comment>('/comments', id);
  }

  // Tags endpoints
  async listTags(): Promise<Tag[]> {
    return await this.list<Tag>('/tags');
  }

  // Spaces endpoints
  async listSpaces(): Promise<Space[]> {
    return await this.list<Space>('/spaces/');
  }
  
  // Get database user_id from Firebase UID
  async getUserDatabaseUUID(firebaseUid: string): Promise<string | null> {
    console.log(`Trying to get database UUID for Firebase UID: ${firebaseUid}`);
    
    // Check if we already have this Firebase UID cached
    if (databaseUuidCache[firebaseUid]) {
      console.log(`Using cached database UUID for Firebase UID ${firebaseUid}: ${databaseUuidCache[firebaseUid]}`);
      return databaseUuidCache[firebaseUid];
    }
    
    try {
      console.log(`Making API call to /users/firebase/${encodeURIComponent(firebaseUid)}`);
      // Correct the expected type to match the actual response { id: string }
      const response = await this.client.get<{ id: string }>(`/users/firebase/${encodeURIComponent(firebaseUid)}`);
      
      // Check if response.data and response.data.id exist before accessing
      if (response?.data?.id) {
        // Correct the console log to access response.data.id
        console.log(`Successfully retrieved database UUID for Firebase UID ${firebaseUid}: ${response.data.id}`);
        // Cache the result for future use
        databaseUuidCache[firebaseUid] = response.data.id;
        // Return the correct field
        return response.data.id;
      } else {
        // Log a warning if the response or id is missing
        console.warn(`API returned a response but no valid ID for Firebase UID ${firebaseUid}`, response?.data);
        return null;
      }
    } catch (error) {
      console.error(`Error fetching database UUID for Firebase UID ${firebaseUid}:`, error);
      
      if (axios.isAxiosError(error)) {
        console.error('Axios error details:', {
          status: error.response?.status,
          data: error.response?.data,
          headers: error.response?.headers,
          config: {
            url: error.config?.url,
            method: error.config?.method
          }
        });
      }
      
      // If we can't get the database UUID, return null to indicate failure
      return null;
    }
  }

  async getUserProfile(userId: string): Promise<UserProfile | null> {
        if (process.env.NODE_ENV === 'development') {
      // Mock implementation for development
      const mockUserProfile = mockUsers.find(user => user.database_uuid === userId);
      
      // Add a mock database UUID for development
      if (mockUserProfile && !mockUserProfile.database_uuid) {
        mockUserProfile.database_uuid = `db-${userId}`;
      }
      
      return mockUserProfile || null;
    }
    
    try {
      const response = await this.client.get<any>(`/users/${userId}`);
      
      console.log('User profile API response:', response.data);
      
      // Transform response fields from snake_case to camelCase
      const transformedProfile: UserProfile = {
        uid: response.data.firebase_uid || '', // Fallback to provided uid if not present
        displayName: response.data.display_name || '',
        connection: response.data.connection || '',
        email: response.data.email || '',
        // The backend returns the database UUID as 'id', not 'uuid'
        database_uuid: response.data.id || userId
      };
      
      // Log what we found for the database_uuid
      console.log('Database UUID extracted from response:', transformedProfile.database_uuid);
      
      // Add created_at if present
      if (response.data.created_at) {
        transformedProfile.createdAt = new Date(response.data.created_at);
      }
      
      return transformedProfile;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        // Explicitly return null only for 404 (user not found)
        console.log(`User profile not found for database UUID: ${userId}`);
        return null;
      }
      // For other errors (like server errors), rethrow to prevent incorrect handling
      console.error('Error fetching user profile:', error);
      throw error;
    }
  }

  async getCurrentUserProfile(): Promise<UserProfile | null> {
    try {
      // Get current user's profile using token-based endpoint
      const response = await this.client.get<any>('/users/profile'); 
      
      console.log('Current user profile API response:', response.data);
      
      // Transform response fields from snake_case to camelCase
      const transformedProfile: UserProfile = {
        uid: response.data.firebase_uid || '',
        displayName: response.data.display_name || '',
        connection: response.data.connection || '',
        email: response.data.email || '',
        database_uuid: response.data.id || null
      };
      
      if (response.data.created_at) {
        transformedProfile.createdAt = new Date(response.data.created_at);
      }
      
      return transformedProfile;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        console.log('Current user profile not found');
        return null;
      }
      console.error('Error fetching current user profile:', error);
      throw error;
    }
  }

  async getUserProfileByUid(uid: string): Promise<UserProfile | null> {
    if (process.env.NODE_ENV === 'development') {
      // Mock implementation for development
      const mockUserProfile = mockUsers.find(user => user.uid === uid);
      
      // Add a mock database UUID for development
      if (mockUserProfile && !mockUserProfile.database_uuid) {
        mockUserProfile.database_uuid = `db-${uid}`;
      }
      
      return mockUserProfile || null;
    }
    
    try {
      // Use the Firebase UID specific endpoint to check if this user exists
      const response = await this.client.get<any>(`/users/firebase/${uid}`); 
      
      console.log('User profile API response:', response.data);
      
      // Transform response fields from snake_case to camelCase
      const transformedProfile: UserProfile = {
        uid: response.data.firebase_uid || uid, // Use the uid from the response or fallback
        displayName: response.data.display_name || '',
        connection: response.data.connection || '',
        email: response.data.email || '',
        // The backend returns the database UUID as 'id'
        database_uuid: response.data.id || null
      };
      
      // Log what we found for the database_uuid
      console.log('Database UUID extracted from response:', transformedProfile.database_uuid);
      
      // Add created_at if present
      if (response.data.created_at) {
        transformedProfile.createdAt = new Date(response.data.created_at);
      }
      
      return transformedProfile;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        // Explicitly return null only for 404 (user not found)
        console.log(`User profile not found for Firebase UID: ${uid}`);
        return null;
      }
      // For other errors (like server errors), rethrow to prevent incorrect handling
      console.error('Error fetching user profile:', error);
      throw error;
    }
  }

  async createUserProfile(userProfile: UserProfile): Promise<UserProfile | null> {
    // if (process.env.NODE_ENV === 'development') {
    //   // Mock implementation for development
    //   const newUser = { ...userProfile, createdAt: new Date() };
    //   mockUsers.push(newUser);
    //   return newUser;
    // }
    
    try {
      // Transform userProfile fields to match backend expectations
      const transformedData = {
        firebase_uid: userProfile.uid,
        display_name: userProfile.displayName,
        connection: userProfile.connection,
        email: userProfile.email
      };
      
      console.log('Sending transformed user profile data:', transformedData);
      
      console.log('[ApiClient] About to execute this.client.post...');
      const response = await this.client.post<any>('/users', transformedData);
      
      console.log('Create user profile response:', response.data);
      
      // Transform the response back to our UserProfile type
      if (response.data) {
        const createdProfile: UserProfile = {
          uid: response.data.firebase_uid || userProfile.uid,
          displayName: response.data.display_name || userProfile.displayName,
          connection: response.data.connection || userProfile.connection,
          email: response.data.email || userProfile.email,
          database_uuid: response.data.id || response.data.uuid || null
        };
        
        console.log('Extracted database UUID from created profile:', createdProfile.database_uuid);
        
        return createdProfile;
      }
      
      return null;
    } catch (error) {
      // Rethrow the error instead of returning null
      console.error('Error creating user profile:', error);
      throw error;
    }
  }

  async updateUserProfile(uid: string, profileData: Partial<UserProfile>): Promise<UserProfile | null> {
    try {
      // Transform profileData fields to match backend expectations
      const transformedData: Record<string, any> = {};
      
      if (profileData.displayName !== undefined) {
        transformedData.display_name = profileData.displayName;
      }
      
      if (profileData.connection !== undefined) {
        transformedData.connection = profileData.connection;
      }
      
      if (profileData.email !== undefined) {
        transformedData.email = profileData.email;
      }
      
      // Use the Firebase UID endpoint since userId is actually a Firebase UID
      const response = await this.client.put<any>(`/users/firebase/${uid}`, transformedData);
      
      // Transform response back to UserProfile format
      if (response.data) {
        return {
          uid: response.data.firebase_uid || uid,
          displayName: response.data.display_name || profileData.displayName || '',
          connection: response.data.connection || profileData.connection || '',
          email: response.data.email || profileData.email || '',
          database_uuid: response.data.id || null
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error updating user profile:', error);
      return null;
    }
  }

  async deleteUserProfile(userId: string): Promise<boolean> {
    try {
      await this.client.delete(`/users/${userId}`);
      return true;
    } catch (error) {
      console.error('Error deleting user profile:', error);
      return false;
    }
  }

  // Add new methods to the ApiClient class
  async getApprovedIdeas(sort: SortOption, filters?: FilterOptions, userId?: string): Promise<Idea[]> {
    if (userId) {
      return this.list<Idea>('/ideas/approved', { sort, ...filters, user_id: userId });
    }
    return this.list<Idea>('/ideas/approved', { sort, ...filters });
  }

  async approveIdea(ideaId: string, approved: boolean = true): Promise<Idea | null> {
    try {
      const response = await this.client.patch<Idea>(`/ideas/${ideaId}/approve`, { approved });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async listUsers(): Promise<UserProfile[]> {
    try {
      const response = await this.client.get<any>('/users');
      
      // Transform response data to match the UserProfile interface
      return response.data.map((user: any) => ({
        uid: user.firebase_uid || '',
        displayName: user.display_name || '',
        connection: user.connection || '',
        email: user.email || '',
        database_uuid: user.id || ''
      }));
    } catch (error) {
      console.error('Error fetching users list:', error);
      throw error;
    }
  }
}

/////////////////////////////////////////////////////////////////////////////////////////////////

// Create API client instance
const apiClient = new ApiClient();

// API simulation with delays for development
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Mock data for development
const mockTags = ['Infrastructure', 'Recreation', 'Business', 'Housing', 'Environment', 'Transportation'];

// Generate random dates between 1 and 30 days ago
const getRandomDate = () => {
  const daysAgo = Math.floor(Math.random() * 30) + 1;
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return date;
};

const createTagObject = (name: string): Tag => ({
  id: `tag-${name.toLowerCase().replace(/\s+/g, '-')}`,
  name: name
});

// Sample ideas
const mockIdeas: Idea[] = [
  {
    id: '1',
    title: 'Community Park with Playground',
    description: 'Create a central park with modern playground equipment, walking paths, and picnic areas to serve as a community gathering place.',
    tags: ['Recreation', 'Environment'].map(createTagObject),
    likes: 45,
    created_at: getRandomDate(),
    comment_count: 5,
    userUpvoted: false,
    user_display_name: 'Maria Johnson'
  },
  {
    id: '2',
    title: 'Mixed-Use Development with Affordable Housing',
    description: 'Develop a mixed-use area with retail on ground floors and a mix of market-rate and affordable housing units above.',
    tags: ['Housing', 'Business'].map(createTagObject),
    likes: 38,
    created_at: getRandomDate(),
    comment_count: 3,
    userUpvoted: false,
    user_display_name: 'David Chen'
  },
  {
    id: '3',
    title: 'Pedestrian-Friendly Main Street',
    description: 'Redesign the main thoroughfare to be more pedestrian-friendly with wider sidewalks, street trees, and traffic calming measures.',
    tags: ['Infrastructure', 'Transportation'].map(createTagObject),
    likes: 27,
    created_at: getRandomDate(),
    comment_count: 2,
    userUpvoted: false,
    user_display_name: 'Thomas Walker'
  },
  {
    id: '4',
    title: 'Innovation Hub and Co-Working Space',
    description: 'Create a modern co-working and innovation center to attract entrepreneurs and remote workers to the area.',
    tags: ['Business'].map(createTagObject),
    likes: 32,
    created_at: getRandomDate(),
    comment_count: 7,
    userUpvoted: false,
    user_display_name: 'Sofia Garcia'
  },
  {
    id: '5',
    title: 'Farmers Market and Event Space',
    description: 'Develop a permanent structure and plaza that can host a weekly farmers market and community events.',
    tags: ['Business', 'Recreation'].map(createTagObject),
    likes: 41,
    created_at: getRandomDate(),
    comment_count: 4,
    userUpvoted: false,
    user_display_name: 'James Wilson'
  },
];

// Sample comments
const mockComments: Record<string, Comment[]> = {
  '1': [
    {
      id: 'c1',
      idea_id: '1',
      user_id: 'Jane Smith',
      comment_text: 'I love this idea! My kids would definitely use this playground.',
      created_at: getRandomDate(),
      updated_at: getRandomDate(),
      email: 'jane.smith@example.com'
    },
    {
      id: 'c2',
      idea_id: '1',
      user_id: 'Michael Johnson',
      comment_text: 'Could we include a dog park section as well?',
      created_at: getRandomDate(),
      updated_at: getRandomDate(),
      email: 'michael.johnson@example.com'
    },
    {
      id: 'c3',
      idea_id: '1',
      user_id: 'Sarah Williams',
      comment_text: 'I would suggest adding more shade structures for hot summer days.',
      created_at: getRandomDate(),
      updated_at: getRandomDate(),
      email: 'sarah.williams@example.com'
    },
  ],
  '2': [
    {
      id: 'c4',
      idea_id: '2',
      user_id: 'Robert Davis',
      comment_text: 'How many affordable units are you thinking?',
      created_at: getRandomDate(),
      updated_at: getRandomDate(),
      email: 'robert.davis@example.com'
    },
    {
      id: 'c5',
      idea_id: '2',
      user_id: 'Emily Brown',
      comment_text: 'This would be great for young professionals in the area!',
      created_at: getRandomDate(),
      updated_at: getRandomDate(),
      email: 'emily.brown@example.com'
    },
  ],
};

// API functions
export const api = {
  apiClient,
  
  // Set the authentication token
  setAuthToken: (token: string | null) => {
    apiClient.setAuthToken(token);
  },
  
  // Helper function to get a valid user ID (database UUID or super user)
  getValidUserId: async (providedId?: string | null, currentUser?: any): Promise<string> => {
    // If we have a valid UUID format, use it directly
    if (providedId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(providedId)) {
      console.log('Using provided valid UUID:', providedId);
      return providedId;
    }

    // If we have a firebase UID, try to get the corresponding database UUID
    const firebaseUid = providedId || (currentUser?.uid) || null;
    
    if (firebaseUid) {
      // Check cache first to avoid repeated API calls
      if (databaseUuidCache[firebaseUid]) {
        console.log('Using cached database UUID for Firebase UID:', databaseUuidCache[firebaseUid]);
        return databaseUuidCache[firebaseUid];
      }
      
      // Try to fetch the database UUID from the API
      try {
        console.log('Fetching database UUID for Firebase UID:', firebaseUid);
        const dbUuid = await apiClient.getUserDatabaseUUID(firebaseUid);
        
        if (dbUuid) {
          // Cache the result for future use
          databaseUuidCache[firebaseUid] = dbUuid;
          console.log('Successfully fetched and cached database UUID:', dbUuid);
          return dbUuid;
        }
      } catch (error) {
        console.error('Error getting database UUID:', error);
      }
    }
    
    // If all else fails, use the super user ID
    console.warn('Falling back to SUPER_USER_ID');
    return SUPER_USER_ID;
  },

  // Helper function to get a valid Firebase UID
  getFirebaseUid: async (providedId?: string | null, currentUser?: any): Promise<string> => {
    return getFirebaseUid(providedId, currentUser);
  },
  
  // Get all available tags
  getTags: async (): Promise<Tag[]> => {
    if (isDevelopment) {
      await delay(300);
      return mockTags.map(tagName => ({
        id: `tag-${tagName.toLowerCase().replace(/\s+/g, '-')}`,
        name: tagName
      }));
    }
    return apiClient.listTags();
  },

  // Get all available spaces for current tenant
  getSpaces: async (): Promise<Space[]> => {
    if (isDevelopment) {
      await delay(300);
      return mockSpaces;
    }
    return apiClient.listSpaces();
  },
  
  // Get database user_id from Firebase UID
  getUserDatabaseUUID: async (firebaseUid: string): Promise<string | null> => {
    if (isDevelopment) {
      // For development, create a mock UUID-format user_id
      await delay(200);
      const mockUuid = `dev-${firebaseUid}-${Date.now().toString(36)}`;
      console.log(`Creating mock database user_id for development:`, mockUuid);
      return mockUuid;
    }
    
    try {
      return await apiClient.getUserDatabaseUUID(firebaseUid);
    } catch (error) {
      console.error('Error fetching database user_id:', error);
      return null;
    }
  },
  
  // Get ideas with filtering and sorting
  getIdeas: async (sort: SortOption, filters?: FilterOptions, userId?: string): Promise<Idea[]> => {
    if (isDevelopment) {
      await delay(500);
      
      let filteredIdeas = [...mockIdeas];
      
      // Apply tag filtering if any tags are selected
      if (filters?.tags && filters.tags.length > 0) {
        filteredIdeas = filteredIdeas.filter(idea => 
          filters.tags.some(tagId => 
            idea.tags?.some(tag => tag.id === tagId) ?? false
          )
        );
      }
      
      // Apply search term filtering if provided
      if (filters?.searchTerm) {
        const term = filters.searchTerm.toLowerCase();
        filteredIdeas = filteredIdeas.filter(idea => 
          idea.title.toLowerCase().includes(term) || 
          idea.description.toLowerCase().includes(term)
        );
      }
      
      // Sort ideas
      if (sort === 'mostUpvoted') {
        filteredIdeas.sort((a, b) => (b.likes || 0) - (a.likes || 0));
      } else if (sort === 'mostRecent') {
        filteredIdeas.sort((a, b) => {
          const dateA = a.created_at instanceof Date ? a.created_at : new Date(a.created_at);
          const dateB = b.created_at instanceof Date ? b.created_at : new Date(b.created_at);
          return dateB.getTime() - dateA.getTime();
        });
      }
      
      return filteredIdeas;
    }
    return apiClient.listIdeas(sort, filters, userId);
  },

  // Get a single idea by ID
  getIdea: async (id: string, userId?: string): Promise<Idea | null> => {
    if (isDevelopment) {
      await delay(300);
      const idea = mockIdeas.find(idea => idea.id === id);
      return idea || null;
    }
    return apiClient.getIdea(id, userId);
  },

  // Delete an idea by ID
  deleteIdea: async (id: string): Promise<void> => {
    if (isDevelopment) {
      await delay(300);
      
      // Find the idea index
      const ideaIndex = mockIdeas.findIndex(idea => idea.id === id);
      if (ideaIndex === -1) {
        throw new Error('Idea not found');
      }
      
      // Remove the idea from mock data
      mockIdeas.splice(ideaIndex, 1);
      
      // Remove all comments for this idea
      delete mockComments[id];
      
      return;
    }
    await apiClient.deleteIdea(id);
  },

  getIdeaComments: async (ideaId: string): Promise<Comment[]> => {
    if (isDevelopment) {
      await delay(400);
      return mockComments[ideaId] || [];
    }
    // TODO: actually filter for idea
    return apiClient.listIdeaComments(ideaId);
  },

  // Get comments for an idea
  getComments: async (ideaId: string): Promise<Comment[]> => {
    if (isDevelopment) {
      await delay(400);
      return mockComments[ideaId] || [];
    }
    // TODO: actually filter for idea
    return apiClient.listComments(ideaId);
  },

  // Delete a comment by ID
  deleteComment: async (id: string, ideaId: string): Promise<void> => {
    if (isDevelopment) {
      await delay(300);
      
      // If mockComments for the idea doesn't exist, return
      if (!mockComments[ideaId]) {
        throw new Error('Idea not found');
      }
      
      // Find the comment index
      const commentIndex = mockComments[ideaId].findIndex(comment => comment.id === id);
      if (commentIndex === -1) {
        throw new Error('Comment not found');
      }
      
      // Remove the comment from mock data
      mockComments[ideaId].splice(commentIndex, 1);
      
      // Update the comments count for the idea
      const idea = mockIdeas.find(idea => idea.id === ideaId);
      if (idea) {
        idea.comment_count -= 1;
      }
      
      return;
    }
    await apiClient.deleteComment(id);
  },


  // Add a new idea
  addIdea: async (idea: Omit<Idea, 'id' | 'upvotes' | 'commentsCount' | 'created_at' | 'likes'>): Promise<Idea> => {
    if (isDevelopment) {
      await delay(600);
      
      const newIdea: Idea = {
        ...idea,
        id: String(mockIdeas.length + 1),
        comment_count: 0,
        created_at: new Date(),
        likes: 0,
        user_id: "bc7f4d80-a27d-4f55-91fb-80a2389704b4",
      };
      
      mockIdeas.push(newIdea);
      return newIdea;
    }
    
    // Store the author name temporarily
    const authorName = idea.user_display_name;
    
    // Prepare the request object
    let ideaRequest: any = {
      title: idea.title,
      description: idea.description,
      tags: idea.tags?.map(tag => ({ name: tag.name })) || []
    };
    
    
    ideaRequest.user_id = idea.user_id;
    
    
    console.log('Formatted request for backend:', JSON.stringify(ideaRequest, null, 2));
    
    // Call API to create the idea
    const createdIdea = await apiClient.createIdea({
      ...idea, // Keep the original object for type satisfaction
      // This will be ignored by createIdea's formatting logic
    });
    
    // Ensure the author is set correctly in the returned object
    if (!createdIdea.user_display_name) {
      createdIdea.user_display_name = authorName;
    }
    
    return createdIdea;
  },

  // Upload image for an idea
  uploadIdeaImage: async (ideaId: string, file: File): Promise<Idea | null> => {
    if (isDevelopment) {
      await delay(800);
      const idea = mockIdeas.find(i => i.id === ideaId);
      if (!idea) return null;
      
      // Simulate setting an image URL - in dev mode, create an object URL
      idea.image_url = URL.createObjectURL(file);
      return idea;
    }
    
    return apiClient.uploadFileToIdea(ideaId, file);
  },

  // Add a comment to an idea
  addComment: async (comment: { idea_id: string; comment_text: string;}): Promise<Comment> => {
    console.log('api.addComment called with:', comment);
    
    if (isDevelopment) {
      // --- Keep the development mock logic ---
      await delay(400);
      const newComment: Comment = {
        ...comment,
        user_id: 'mock-user-id', // Assign a mock ID for dev display
        id: `c${Date.now()}`,
        created_at: new Date(),
        updated_at: new Date(),
      };
      if (!mockComments[comment.idea_id]) {
        mockComments[comment.idea_id] = [];
      }
      mockComments[comment.idea_id].push(newComment);
      const idea = mockIdeas.find(idea => idea.id === comment.idea_id);
      if (idea) {
        idea.comment_count += 1;
      }
      return newComment;
      // --- End development mock logic ---
    }
    
    // The backend handles user identification via the auth token
    
    // Prepare data for the API client (only idea_id and text)
    const commentData = { 
        idea_id: comment.idea_id, 
        comment_text: comment.comment_text,
    };
    
    // Call the simplified apiClient.createComment
    console.log('Calling apiClient.createComment with:', commentData);
    const result = await apiClient.createComment(commentData);
    console.log('Result from apiClient.createComment:', result);
    return result;
  },

  // Toggle upvote for an idea
  toggleUpvote: async (ideaId: string, likedByUser: boolean | undefined = undefined): Promise<Idea | null> => {
    
    // Ensure likedByUser is explicitly a boolean
    const like = likedByUser === undefined ? false : likedByUser;
    
    if (isDevelopment) {
      await delay(300);
      
      const idea = mockIdeas.find(idea => idea.id === ideaId);
      if (!idea) throw new Error('Idea not found');
      
      idea.userUpvoted = !idea.userUpvoted;
      idea.likes += idea.userUpvoted ? 1 : -1;
      
      return idea;
    }
    return apiClient.toggleUpvote(ideaId, like);
  },

  getUserProfile: async (userId: string): Promise<UserProfile | null> => {
    if (process.env.NODE_ENV === 'development') {
      // Mock implementation for development
      const mockUserProfile = mockUsers.find(user => user.uid === userId);
      
      // Add a mock database UUID for development
      if (mockUserProfile && !mockUserProfile.database_uuid) {
        mockUserProfile.database_uuid = `db-${userId}`;
      }
      
      return mockUserProfile || null;
    }
    
    try {
      const response = await apiClient.getUserProfile(userId);
      return response;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        // Explicitly return null only for 404 (user not found)
        console.log(`User profile not found for database UUID: ${userId}`);
        return null;
      }
      // For other errors (like server errors), rethrow to prevent incorrect handling
      console.error('Error fetching user profile:', error);
      throw error;
    }
  },

  getUserProfileByUid: async (uid: string): Promise<UserProfile | null> => {
    if (process.env.NODE_ENV === 'development') {
      // Mock implementation for development
      const mockUserProfile = mockUsers.find(user => user.uid === uid);
      
      // Add a mock database UUID for development
      if (mockUserProfile && !mockUserProfile.database_uuid) {
        mockUserProfile.database_uuid = `db-${uid}`;
      }
      
      return mockUserProfile || null;
    }
    
    try {
      // Fetch the current user's profile using the token (no need for UID in URL)
      // Assuming the endpoint is /users/profile
      const response = await apiClient.getUserProfileByUid(uid); 
      return response; // Directly return the response, as transformation is done in ApiClient
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        // Explicitly return null only for 404 (user not found)
        console.log(`User profile not found for current user (via token)`);
        return null;
      }
      // For other errors (like server errors), rethrow to prevent incorrect handling
      console.error('Error fetching user profile:', error);
      throw error;
    }
  },

  createUserProfile: async (userProfile: UserProfile): Promise<UserProfile | null> => {
    // if (process.env.NODE_ENV === 'development') {
    //   // Mock implementation for development
    //   const newUser = { ...userProfile, createdAt: new Date() };
    //   mockUsers.push(newUser);
    //   return newUser;
    // }
    
    try {
      const createdProfile = await apiClient.createUserProfile(userProfile);
      console.log('User profile created successfully:', createdProfile);
      return createdProfile;
    } catch (error) {
      console.error('Error creating user profile:', error);
      throw error;
    }
  },

  updateUserProfile: async (userId: string, profileData: Partial<UserProfile>): Promise<UserProfile | null> => {
    if (process.env.NODE_ENV === 'development') {
      // Mock implementation for development
      const userIndex = mockUsers.findIndex(user => user.uid === userId);
      if (userIndex >= 0) {
        mockUsers[userIndex] = { ...mockUsers[userIndex], ...profileData };
        return mockUsers[userIndex];
      }
      return null;
    }
    
    try {
      const response = await apiClient.updateUserProfile(userId, profileData);
      return response;
    } catch (error) {
      console.error('Error updating user profile:', error);
      return null;
    }
  },

  deleteUserProfile: async (userId: string): Promise<boolean> => {
    if (process.env.NODE_ENV === 'development') {
      // Mock implementation for development
      const userIndex = mockUsers.findIndex(user => user.uid === userId);
      if (userIndex >= 0) {
        mockUsers.splice(userIndex, 1);
        return true;
      }
      return false;
    }
    
    try {
      return await apiClient.deleteUserProfile(userId);
    } catch (error) {
      console.error('Error deleting user profile:', error);
      return false;
    }
  },

  // Add wrapper methods in the api object
  getApprovedIdeas: async (sort: SortOption, filters?: FilterOptions, userId?: string): Promise<Idea[]> => {
    if (isDevelopment) {
      await delay(500);
      
      let filteredIdeas = [...mockIdeas].filter(idea => idea.approved); // Only show approved ideas
      
      // Apply existing filtering logic...
      
      return filteredIdeas;
    }
    return apiClient.getApprovedIdeas(sort, filters, userId);
  },

  approveIdea: async (ideaId: string, approved: boolean = true): Promise<Idea | null> => {
    if (isDevelopment) {
      await delay(300);
      
      const idea = mockIdeas.find(idea => idea.id === ideaId);
      if (!idea) throw new Error('Idea not found');
      
      idea.approved = approved;
      return idea;
    }
    return apiClient.approveIdea(ideaId, approved);
  },

  getUsers: async (): Promise<UserProfile[]> => {
    if (isDevelopment) {
      await delay(300);
      return mockUsers;
    }
    
    try {
      return await apiClient.listUsers();
    } catch (error) {
      console.error('Error fetching users list:', error);
      return [];
    }
  },
};

// Mock users for development
const mockUsers: UserProfile[] = [
  {
    uid: 'user123',
    displayName: 'Demo User',
    connection: 'Student at University',
    email: 'demo@example.com'
  },
  {
    uid: 'user456',
    displayName: 'Test User',
    connection: 'Faculty Member',
    email: 'test@example.com'
  }
];

// Mock spaces for development - simulating Evanston's 3 spaces
const mockSpaces: Space[] = [
  {
    id: 'space-1',
    name: 'Police/Fire Station',
    slug: 'police-fire-station',
    description: 'Ideas for the redevelopment of the police and fire station facility',
    tenant_id: 'evanston',
    created_at: new Date()
  },
  {
    id: 'space-2',
    name: 'Noyes Cultural Arts Center',
    slug: 'noyes-cultural-center',
    description: 'Ideas for enhancing the Noyes Cultural Arts Center',
    tenant_id: 'evanston',
    created_at: new Date()
  },
  {
    id: 'space-3',
    name: 'Civic Center (Old City Hall)',
    slug: 'civic-center',
    description: 'Ideas for the transformation of the old city hall into a civic center',
    tenant_id: 'evanston',
    created_at: new Date()
  }
];

// Helper function to ensure we have a Firebase UID
const getFirebaseUid = async (providedId?: string | null, currentUser?: any): Promise<string> => {
  // If null/undefined, use current user's Firebase UID if available
  if (!providedId && currentUser?.uid) {
    console.log('Using current user Firebase UID:', currentUser.uid);
    return currentUser.uid;
  }
  
  // If provided ID looks like a Firebase UID (not UUID format), use it directly
  if (providedId && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(providedId)) {
    console.log('Using provided Firebase UID:', providedId);
    return providedId;
  }
  
  // If we have a database UUID but need a Firebase UID, we'd need to look it up (not implemented yet)
  // For now, fall back to using current user's UID or super user
  if (currentUser?.uid) {
    console.log('Falling back to current user Firebase UID:', currentUser.uid);
    return currentUser.uid;
  }
  
  // Last resort - use super user ID
  console.log('Falling back to SUPER USER Firebase UID for auth');
  return ''; // Use a default Firebase UID format
}; 